import React, { useState, useCallback, useRef, useEffect } from 'react';
import { uppercaseLabel } from '../styles/typography';
import {
    Button,
    Box,
    Typography,
    CircularProgress,
    Snackbar,
    Alert,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Popover,
    Tooltip,
} from '@mui/material';
import Papa from 'papaparse';
import useImportColumnConfirmation from '../hooks/useImportColumnConfirmation';
import { inspectPortfolioColumns, readFirstWorksheet, readImportMatrix } from '../utils/importColumnReview';
import { useImportFileTransfer } from '../context/ImportFileTransferContext';
import { useSecurityOperationContext } from '../context/SecurityOperationContext';
import { isPastedSheet, pastedSheetFile } from './watchlist_table/pastedSheet';
import { PasteFromExcelDialog } from './shared/SheetPaste';
import * as XLSX from 'xlsx';
import { callBackend } from '../utils/api';
import { parseCloudRunError } from './msiDriftReviewHelpers';
import PortfolioImportWatchlistReview from './PortfolioImportWatchlistReview';
import {
    PORTFOLIO_IDENTIFIER_COLUMNS,
} from '../config/UploadConstant';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import {
    APP_STYLE_COMPONENT_COLORS,
    APP_STYLE_PALETTE,
    MSI_GEOMETRY,
} from '../styles/msiStyleTokens';
import {
    watchlistPopoverPaperSx,
    watchlistSecondaryControlSx,
    watchlistSemanticFrameSx,
} from '../styles/watchlistMsiStyles';

const compactIconControlSx = [
    { border: '1px solid', borderColor: 'divider' },
    watchlistSecondaryControlSx,
    { borderRadius: `${MSI_GEOMETRY.radii.iconButton}px` },
];

const compactDangerIconControlSx = [
    ...compactIconControlSx,
    {
        color: APP_STYLE_PALETTE.danger,
        '&:hover': {
            borderColor: APP_STYLE_COMPONENT_COLORS.semanticBorders.danger,
            backgroundColor: APP_STYLE_PALETTE.dangerBg,
            color: APP_STYLE_PALETTE.danger,
        },
    },
];

function portfolioCleanupSummary(inputValidation) {
    const validation = inputValidation && typeof inputValidation === 'object'
        ? inputValidation
        : {};
    const errors = Array.isArray(validation.errors) ? validation.errors : [];
    const aggregateCodes = validation.error_code_counts
        && typeof validation.error_code_counts === 'object'
        ? validation.error_code_counts
        : {};
    const skipped = Math.max(
        errors.filter(
            (error) => error?.code === 'portfolio_upload_identifier_missing',
        ).length,
        Number(aggregateCodes.portfolio_upload_identifier_missing) || 0,
    );
    const blankRows = Number(validation.blank_row_count_removed) || 0;
    const blankColumns = Number(validation.blank_column_count_removed) || 0;
    const parts = [];
    if (skipped > 0) parts.push(`${skipped} blank-identifier row${skipped === 1 ? '' : 's'} skipped`);
    if (blankRows > 0) parts.push(`${blankRows} fully blank row${blankRows === 1 ? '' : 's'} ignored`);
    if (blankColumns > 0) parts.push(`${blankColumns} fully blank unnamed column${blankColumns === 1 ? '' : 's'} ignored`);
    return parts.length ? ` ${parts.join('; ')}.` : '';
}

function downloadPortfolioTemplateCSV(e) {
    e.preventDefault();
    const csv = [
        'cusip,quantity',
        '037833100,100',
        '594918104,50',
        '02079K305,75',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'portfolio_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function downloadPortfolioTemplateExcel(e) {
    e.preventDefault();
    const data = [
        ['cusip', 'quantity'],
        ['037833100', '100'],
        ['594918104', '50'],
        ['02079K305', '75'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Portfolio');
    XLSX.writeFile(wb, 'portfolio_template.xlsx');
}

function DynamicTable({ data }) {
    if (!Array.isArray(data) || data.length === 0) return null;
    let headers = [];
    let rows = [];

    if (Array.isArray(data[0])) {
        headers = data[0];
        rows = data.slice(1);
    } else if (data[0] && typeof data[0] === 'object') {
        headers = Object.keys(data[0]);
        rows = data.map(row => headers.map(h => row?.[h] ?? ''));
    } else {
        headers = ['Value'];
        rows = data.map(item => [String(item ?? '')]);
    }

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px', color: 'var(--app-ink700)' }}>
            <thead>
                <tr>
                    {headers.map((h, idx) => (
                        <th key={idx} style={{ border: '1px solid var(--app-lineDark)', padding: '4px', background: 'var(--app-tableHeader)', color: 'var(--app-ink500)' }}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, rIdx) => (
                    <tr key={rIdx}>
                        {row.map((cell, cIdx) => (
                            <td key={cIdx} style={{ border: '1px solid var(--app-rowDivider)', padding: '4px', background: 'var(--app-surface)' }}>{cell}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default function PortfolioUploader({
    selectedFactorModel,
    serverNormalVol,
    serverMaxPosSize,
    aum,
    default_target_vol_pct,
    default_target_vol_dollar,
    default_scaler,
    onUploadSuccess,
    setActualPortfolioVersion,
    compact = false,
    acceptTransferredFile = false,
}) {
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [previewData, setPreviewData] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [infoAnchorEl, setInfoAnchorEl] = useState(null);
    const [watchlistReview, setWatchlistReview] = useState(null);
    const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
    const fileInputRef = useRef(null);
    const selectedFileRef = useRef(null);
    const generationRef = useRef(0);
    const { sessionScopeToken } = useSecurityOperationContext();
    const { pending, claim } = useImportFileTransfer();
    const { confirmColumns, columnDialog, cancelColumnConfirmation } = useImportColumnConfirmation('portfolio');
    useEffect(() => {
        selectedFileRef.current = null;
        generationRef.current += 1;
        setFile(null);
        setFileName('');
        setPreviewData(null);
        setWatchlistReview(null);
        setPasteDialogOpen(false);
        setUploading(false);
        setError('');
        setSuccess('');
        return () => { generationRef.current += 1; };
    }, [sessionScopeToken]);

    const parseFileForPreview = useCallback((selectedFile) => {
        cancelColumnConfirmation();
        setWatchlistReview(null);
        setUploading(false);
        const generation = ++generationRef.current;
        selectedFileRef.current = selectedFile;
        const ext = selectedFile.name.split('.').pop().toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(ext)) {
            setError('Upload a valid CSV or Excel file.');
            selectedFileRef.current = null;
            setFile(null);
            setFileName('');
            setPreviewData(null);
            return;
        }
        setFile(selectedFile);
        setFileName(selectedFile.name);
        setError('');
        setSuccess('');
        setPreviewData(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            if (generation !== generationRef.current) return;
            const buf = e.target.result;
            if (ext === 'csv') {
                const text = new TextDecoder('utf-8').decode(buf);
                const { data } = Papa.parse(text, { header: true, skipEmptyLines: true });
                setPreviewData(data.slice(0, 5));
            } else {
                try {
                    const ws = readFirstWorksheet(buf);
                    const arr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                    setPreviewData(arr.slice(0, 6));
                } catch {
                    setError('Failed to parse Excel preview.');
                }
            }
        };
        reader.readAsArrayBuffer(selectedFile);
    }, [cancelColumnConfirmation]);

    const handleFileChange = (e) => {
        const f = e.target.files[0];
        if (f) parseFileForPreview(f);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files[0];
        if (f) parseFileForPreview(f);
    };

    const processConfirmedImport = async (request, generation) => {
        const response = await callBackend('process_portfolio_file', 'POST', request);
        if (generation !== generationRef.current) return;
        if (!response?.success) throw new Error(response?.message || 'Server processing failed.');
        if (response.status === 'watchlist_review_required') {
            setWatchlistReview({ request, response, generation });
            return;
        }
        setWatchlistReview(null);
        // Holdings that already match Actual are still staged: accepting them
        // resets Position Sizing to these holdings.
        setSuccess(
            response.changed === false
                ? `Upload staged. Your Actual Portfolio already matches this file. Accept the pending import to reset Position Sizing to these holdings.${portfolioCleanupSummary(response.input_validation)}`
                : `Upload staged. Review the pending import before applying.${portfolioCleanupSummary(response.input_validation)}`,
        );
        setFile(null);
        setFileName('');
        setPreviewData(null);
        selectedFileRef.current = null;
        if (fileInputRef.current) fileInputRef.current.value = '';
        onUploadSuccess?.();
    };

    const continueImport = async (request) => {
        const generation = generationRef.current;
        setWatchlistReview(null);
        setUploading(true);
        setError('');
        try {
            await processConfirmedImport(request, generation);
        } catch (err) {
            if (generation === generationRef.current) setError(parseCloudRunError(err).message || 'Portfolio upload failed.');
        } finally {
            if (generation === generationRef.current) setUploading(false);
        }
    };

    const handleUpload = async (incomingFile = selectedFileRef.current) => {
        const file = incomingFile;
        const generation = generationRef.current;
        if (!file) {
            setError('No file selected.');
            return;
        }
        setUploading(true);
        setError('');
        setSuccess('');
        try {
            const rawMatrix = await readImportMatrix(file);
            if (generation !== generationRef.current) return;
            const confirmed = await confirmColumns({ review: inspectPortfolioColumns(rawMatrix), sourceLabel: file.name, file });
            if (!confirmed || generation !== generationRef.current) return;
            const { matrix, identifierColumn } = confirmed;
            await processConfirmedImport({
                matrix,
                upload_attempt_id: crypto.randomUUID(),
                ...(identifierColumn ? { identifier_column: identifierColumn } : {}),
                normal_vol: serverNormalVol,
                max_pos_size: serverMaxPosSize,
                aum,
            }, generation);
        } catch (err) {
            if (generation !== generationRef.current) return;
            console.error('Upload error:', err);
            setError(parseCloudRunError(err).message || 'Portfolio upload failed.');
        } finally {
            if (generation === generationRef.current) setUploading(false);
        }
    };

    // A pasted sheet is a portfolio import, like an uploaded file: the same
    // column check, review and Apply (see watchlist_table/pastedSheet.js).
    const importPastedSheet = (pastedRows) => {
        if (!isPastedSheet(pastedRows, 'portfolio')) return false;
        const pasted = pastedSheetFile(pastedRows);
        parseFileForPreview(pasted);
        handleUpload(pasted);
        return true;
    };

    useEffect(() => {
        if (!acceptTransferredFile || pending?.destination !== 'portfolio') return;
        const transferred = claim(pending);
        if (!transferred) return;
        parseFileForPreview(transferred);
        handleUpload(transferred);
    }, [acceptTransferredFile, pending, claim, parseFileForPreview]);

    const handlePreviewOpen = () => setPreviewOpen(true);
    const handlePreviewClose = () => setPreviewOpen(false);
    const handleCancel = () => {
        generationRef.current += 1;
        selectedFileRef.current = null;
        cancelColumnConfirmation();
        setWatchlistReview(null);
        setUploading(false);
        setFile(null);
        setFileName('');
        setPreviewData(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const dropZoneWidth = compact ? 96 : 230;
    const dropZoneHeight = compact ? 34 : 40;

    return (
        <>
            {columnDialog}
            <PasteFromExcelDialog
                open={pasteDialogOpen}
                destination="portfolio"
                onImport={importPastedSheet}
                onClose={() => setPasteDialogOpen(false)}
            />
            {watchlistReview && <PortfolioImportWatchlistReview
                key={`${watchlistReview.generation}:${watchlistReview.response.current_watchlist_revision_id}`}
                review={watchlistReview} onContinue={continueImport}
                onCancel={() => setWatchlistReview(null)}
            />}
            {!compact && (
                <Typography
                    variant="subtitle1"
                    sx={{
                        mb: 1,
                        width: '100%',
                    }}
                >
                    Upload Portfolio
                </Typography>
            )}
            <Box sx={{ p: 0, width: '100%' }}>
                {!compact && (
                <Typography variant="body2" gutterBottom>
                        Required columns: <strong>quantity</strong> and an identifier column (<strong>{PORTFOLIO_IDENTIFIER_COLUMNS.join(', ')}</strong>). Add optional <strong>country</strong> to specify an exact listing. Bloomberg equity tickers can use a <strong>BBG</strong> column, for example <strong>AAPL US Equity</strong>. If the file contains multiple identifier columns, you will choose which one to use.&nbsp;
                        Download Sample:&nbsp;
                        <Link onClick={downloadPortfolioTemplateCSV} sx={{ cursor: 'pointer' }}>CSV</Link> or&nbsp;
                        <Link onClick={downloadPortfolioTemplateExcel} sx={{ cursor: 'pointer' }}>Excel</Link>.
                    </Typography>
                )}

                {compact ? (
                    <>
                        <Box display="flex" alignItems="center" gap={0.65} flexWrap="wrap" sx={{ mb: 0.2 }}>
                            <Tooltip title="Choose File">
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={compactIconControlSx}
                                    >
                                        <InsertDriveFileIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Paste from Excel">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => setPasteDialogOpen(true)}
                                    aria-label="Paste from Excel"
                                    sx={compactIconControlSx}
                                >
                                    <ContentPasteOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Upload">
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleUpload()}
                                        disabled={uploading || Boolean(watchlistReview) || !file}
                                        sx={compactIconControlSx}
                                    >
                                        {uploading ? <CircularProgress size={16} /> : <CloudUploadIcon fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Preview">
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={handlePreviewOpen}
                                        disabled={!file}
                                        sx={compactIconControlSx}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Drop File Here">
                                <Box
                                    sx={[{
                                        border: '1px dashed',
                                        borderColor: dragOver ? 'primary.main' : 'grey.400',
                                        width: fileName ? 158 : 120,
                                        height: 30,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        cursor: 'pointer',
                                        bgcolor: dragOver ? 'action.hover' : 'rgba(0,0,0,0.04)',
                                        transition: 'background-color 0.2s',
                                        px: 1,
                                        gap: 0.75,
                                    }, watchlistSemanticFrameSx(dragOver ? 'info' : 'neutral')]}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <UploadFileIcon fontSize="small" color={dragOver ? 'primary' : 'action'} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.66rem',
                                            color: fileName ? 'text.primary' : 'text.secondary',
                                            lineHeight: 1.2,
                                        }}
                                        noWrap
                                    >
                                        {fileName || 'Drop file'}
                                    </Typography>
                                </Box>
                            </Tooltip>

                            <Tooltip title="Cancel">
                                <span>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={handleCancel}
                                        disabled={!file}
                                        sx={compactDangerIconControlSx}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Import requirements">
                                <IconButton
                                    size="small"
                                    color="default"
                                    onClick={(event) => setInfoAnchorEl(event.currentTarget)}
                                    sx={compactIconControlSx}
                                >
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            hidden
                            onChange={handleFileChange}
                        />

                        <Popover
                            open={Boolean(infoAnchorEl)}
                            anchorEl={infoAnchorEl}
                            onClose={() => setInfoAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{ sx: [{ p: 1.25, maxWidth: 280 }, watchlistPopoverPaperSx] }}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                                Upload a CSV or Excel file, or paste a sheet copied from Excel, with required column <strong>quantity</strong>, an identifier column (<strong>{PORTFOLIO_IDENTIFIER_COLUMNS.join(', ')}</strong>), and optional <strong>country</strong>. If multiple identifier columns are present, you will choose which one identifies the securities.
                            </Typography>
                            <Box display="flex" gap={0.75} sx={{ mt: 1 }}>
                                <Link
                                    onClick={(event) => {
                                        downloadPortfolioTemplateCSV(event);
                                        setInfoAnchorEl(null);
                                    }}
                                    sx={{ cursor: 'pointer', ...uppercaseLabel({ fontSize: '0.72rem' }) }}
                                >
                                    CSV Template
                                </Link>
                                <Link
                                    onClick={(event) => {
                                        downloadPortfolioTemplateExcel(event);
                                        setInfoAnchorEl(null);
                                    }}
                                    sx={{ cursor: 'pointer', ...uppercaseLabel({ fontSize: '0.72rem' }) }}
                                >
                                    Excel Template
                                </Link>
                            </Box>
                        </Popover>
                    </>
                ) : (
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                        {/* Drop zone */}
                        <Box
                            sx={[{
                                border: '2px dashed',
                                borderColor: dragOver ? 'primary.main' : 'grey.400',
                                width: dropZoneWidth,
                                height: dropZoneHeight,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                bgcolor: dragOver ? 'action.hover' : 'rgba(0,0,0,0.04)',
                                transition: 'background-color 0.2s',
                                px: 1,
                            }, watchlistSemanticFrameSx(dragOver ? 'info' : 'neutral')]}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Typography variant={compact ? 'caption' : 'body2'} color={dragOver ? 'primary' : 'textPrimary'}>
                                {fileName || 'Drop file here'}
                            </Typography>
                        </Box>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            hidden
                            onChange={handleFileChange}
                        />

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Choose File
                        </Button>

                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleUpload()}
                            disabled={uploading || Boolean(watchlistReview) || !file}
                        >
                            {uploading ? <CircularProgress size={16} /> : 'Upload'}
                        </Button>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handlePreviewOpen}
                            disabled={!file}
                        >
                            Preview
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCancel}
                            disabled={!file}
                        >
                            Cancel
                        </Button>
                    </Box>
                )}

                <Dialog open={previewOpen} onClose={handlePreviewClose} maxWidth="xs" fullWidth>
                    <DialogTitle>Preview</DialogTitle>
                    <DialogContent dividers>
                        <Box
                            sx={{
                                fontSize: '0.75rem',
                                fontFamily: 'monospace',
                                m: 0,
                                maxHeight: 300,
                                overflow: 'auto',
                            }}
                        >
                            {Array.isArray(previewData)
                                ? <DynamicTable data={previewData} />
                                : JSON.stringify(previewData, null, 2)}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button size="small" onClick={handlePreviewClose}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {error && (
                    <Snackbar open autoHideDuration={6000} onClose={() => setError('')}>
                        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>
                            {error}
                        </Alert>
                    </Snackbar>
                )}
                {success && (
                    <Snackbar open autoHideDuration={6000} onClose={() => setSuccess('')}>
                        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
                            {success}
                        </Alert>
                    </Snackbar>
                )}
            </Box>
        </>
    );
}
